import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { clearOrder, completeOrder, getOrder, loadMemberIfSignedIn, logger, orderHasInProgressAction, orderRequiresPayment, temporaryOrderDomainStore } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { PaymentFailedErrorResultCode } from '@vista-digital/embedded-payments';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { Button } from '../../subcomponents/button/button';
import { ButtonColor } from '../../subcomponents/button/types/color';
import { ButtonSize } from '../../subcomponents/button/types/size';
import { withComponentWrapper } from '../../utils/with-component-wrapper';
import { OrderPaymentFormMessage, OrderPaymentFormMessageType } from './subcomponents/message';
import { OrderPaymentFormMethods } from './subcomponents/methods';
import { OrderPaymentFormSummary } from './subcomponents/summary';
import { mapOrderPaymentFormContext, OrderPaymentFormContextInstance } from './types/context';
import { defaultOrderPaymentFormProps, OrderPaymentFormPropsValidator } from './types/props';
const b = bemClassBuilder('v-order-payment-form');
/**
 * Renders a form that allows a user to pay for an in-progress order.
 */
export const OrderPaymentForm = observer(props => {
    const { displayText, events } = props;
    const [hasOrderCompletionFailed, setHasOrderCompletionFailed] = useState(false);
    const order = getOrder();
    if (!order)
        return null; // cannot render without an in-progress order
    const handlePaymentSuccessful = async (orderCompletion) => {
        var _a;
        (_a = events.onPaymentSuccessful) === null || _a === void 0 ? void 0 : _a.call(events, orderCompletion);
        clearOrder();
        temporaryOrderDomainStore.reset();
        await loadMemberIfSignedIn(true);
    };
    const handlePaymentFailed = (error) => {
        var _a;
        if (error.resultCode === PaymentFailedErrorResultCode.OrderCompletionFailed) {
            logger.error(`Payment failed. Order ${order.id} not completed in Connect.`);
            setHasOrderCompletionFailed(true);
        }
        (_a = events.onPaymentFailed) === null || _a === void 0 ? void 0 : _a.call(events, error);
    };
    // TODO: Removing a card will cause the payment form to rerender. To fix, memoise context and change handleCallbacks to use dispatch as handle remove has a dependency on state
    // https://reactjs.org/docs/hooks-faq.html#how-to-read-an-often-changing-value-from-usecallback
    const orderPaymentFormContext = mapOrderPaymentFormContext(props, handlePaymentSuccessful, handlePaymentFailed);
    const handleCompleteOrderButtonClicked = async () => {
        var _a;
        try {
            (_a = events.onPaymentStarted) === null || _a === void 0 ? void 0 : _a.call(events);
            const orderCompletion = await completeOrder();
            await handlePaymentSuccessful(orderCompletion);
        }
        catch (error) {
            setHasOrderCompletionFailed(true);
        }
    };
    if (hasOrderCompletionFailed) {
        return (_jsx(OrderPaymentFormContextInstance.Provider, Object.assign({ value: orderPaymentFormContext }, { children: _jsx(OrderPaymentFormMessage, { messageType: OrderPaymentFormMessageType.OrderCompletionFailed }) })));
    }
    const isPaymentRequired = orderRequiresPayment();
    return (_jsx("div", Object.assign({ className: b.block() }, { children: _jsxs(OrderPaymentFormContextInstance.Provider, Object.assign({ value: orderPaymentFormContext }, { children: [_jsx(OrderPaymentFormSummary, {}), isPaymentRequired ? (_jsx(OrderPaymentFormMethods, { order: order })) : (_jsx(Button, { color: ButtonColor.Primary, size: ButtonSize.Medium, label: displayText.completeOrderButtonLabel, loading: orderHasInProgressAction(), onClick: handleCompleteOrderButtonClicked, className: b.element('complete-order') }))] })) })));
});
OrderPaymentForm.displayName = 'OrderPaymentForm';
/**
 * The **Order Payment Form** component allows a user to pay for an in-progress order.
 */
export const OrderPaymentFormComponent = withComponentWrapper(OrderPaymentForm, defaultOrderPaymentFormProps, OrderPaymentFormPropsValidator);
